import { useEffect, useMemo } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { articles } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  const article = useMemo(
    () => articles.find((a) => a.id === id),
    [id]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter(
              (a) =>
                a.categorySlug === article.categorySlug && a.id !== article.id
            )
            .slice(0, 3)
        : [],
    [article]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">
              文章未找到
            </h1>
            <p className="text-foreground-600 text-sm mb-6">
              您要查看的文章可能已被移除或链接无效
            </p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            datePublished: article.date,
            author: {
              "@type": "Person",
              name: article.author,
            },
            publisher: {
              "@type": "Organization",
              name: "佐佐木希官方粉丝资讯站",
            },
            url: `https://cfcfo.cn/article/${article.id}`,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://cfcfo.cn/article/${article.id}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://cfcfo.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: article.category,
                item: `https://cfcfo.cn/category/${article.categorySlug}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-20 bg-background-50">
        <article className="max-w-screen-lg mx-auto px-4 md:px-6">
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                {
                  label: article.category,
                  path: `/category/${article.categorySlug}`,
                },
                { label: article.title },
              ]}
            />
          </div>

          <header className="py-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-sm text-foreground-500">{article.date}</span>
            </div>
            <h1
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {article.title}
            </h1>
            <p className="text-lg text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
              <span>作者：{article.author}</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.viewCount.toLocaleString()} 次阅读
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full object-cover object-top max-h-[480px]"
              title={article.title}
            />
          </div>

          <div
            className="prose prose-lg max-w-none pb-12"
            dangerouslySetInnerHTML={{
              __html: article.content
                .split("\n")
                .filter((line) => line.trim())
                .map(
                  (line) =>
                    `<p class="text-foreground-700 leading-relaxed mb-4 text-base">${line.replace(/<\/?p>/g, "")}</p>`
                )
                .join(""),
            }}
          />

          <div className="flex flex-wrap items-center gap-2 py-6 border-t border-background-200/70">
            <span className="text-sm text-foreground-500 mr-2">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <section className="py-10 border-t border-background-200/70">
              <h2
                className="text-xl font-bold text-foreground-950 mb-6"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group rounded-lg border border-background-200/70 hover:border-background-300 overflow-hidden transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">
                        {ra.date}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}